import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Card,
  CardContent,
  Grid,
  Container,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import { apiSettings } from "../../config/images";
import { getPrivacyPolicies, updatePrivacyPolicy } from "./services";

const PrivacyPolicy = () => {
  const [rightContent, setRightContent] = useState("");
  const [leftContent, setLeftContent] = useState("");
  const [privacyPolicyId, setPrivacyPolicyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [editEnabled, setEditEnabled] = useState(false);

  // Fetch privacy policy on component mount
  useEffect(() => {
    fetchPrivacyPolicy();
  }, []);

  const fetchPrivacyPolicy = async () => {
    try {
      setLoading(true);
      const response = await getPrivacyPolicies();

      if (response.data.success && response.data.payload.length > 0) {
        const policy = response.data.payload[0]; // Get the first policy
        setRightContent(policy.content);
        setPrivacyPolicyId(policy.id);
        // toast.success("Privacy policy loaded successfully!");
      } else {
        // If no policy exists, show default content
        setRightContent(
          "Privacy Policy\n\nNo privacy policy content available. Please add content using the editor on the left."
        );
        toast.info(
          "No privacy policy found. You can create one using the editor.",
          { toastId: "no-policy-found" }
        );
      }
    } catch (error) {
      console.error("Error fetching privacy policy:", error);
      setRightContent(
        "Privacy Policy\n\nError loading privacy policy. Please try again later."
      );
      toast.error("Failed to load privacy policy. Please try again.", {
        toastId: "fetch-policy-error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to handle the "Edit" button click.
  const handleEdit = () => {
    // Copy the content from the right side to the left text field.
    setLeftContent(rightContent);
    // Set editEnabled to true to show the "Update" button.
    setEditEnabled(true);
    // Clear the right content to show the "editing" state visually.
    setRightContent("");
    toast.info("Content loaded in editor mode.", { toastId: "edit-mode" });
  };

  // Function to handle the "Update" button click.
  const handleUpdate = async () => {
    if (!leftContent.trim()) {
      toast.error("Please add content to update.", { toastId: "add-content" });
      return;
    }
    try {
      setUpdating(true);

      // Prepare the request body according to the specified format
      const requestBody = {
        content: leftContent,
      };

      // Call the update API
      const response = await updatePrivacyPolicy(requestBody);

      if (response.data.success) {
        // Update the right side with the edited content from the left.
        setRightContent(leftContent);
        // Clear the left text field.
        setLeftContent("");
        toast.success("Privacy policy updated successfully!", {
          toastId: "update-success",
        });

        // Call get API after successful update to refresh data
        await fetchPrivacyPolicy();
      } else {
        toast.error(
          "Failed to update privacy policy: " +
            (response.data.message || "Unknown error"),
          { toastId: "update-error" }
        );
      }
    } catch (error) {
      console.error("Error updating privacy policy:", error);
      toast.error(
        error.response?.data?.message || "Failed to update privacy policy",
        { toastId: "update-error" }
      );
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#eaf3fc",
        minHeight: "94vh",
        padding: { xs: "14px", md: "36px" },
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontSize: { xs: "24px", md: "36px", sm: "32px" },
          fontWeight: 600,
        }}
      >
        Privacy Policy
      </Typography>

      {/* Two-column layout for editing and viewing */}
      <Grid container spacing={2} sx={{ pt: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: "16px",
              fontWeight: 600,
              color: "#333333",
              mb: 1,
            }}
          >
            Add Content
          </Typography>
          <Card
            sx={{
              borderRadius: "8px",
              padding: 3,
              display: "flex",
              flexDirection: "column",
              height: "75vh",
              position: "relative",
            }}
          >
            {updating && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10,
                  borderRadius: "8px",
                }}
              >
                <CircularProgress size={40} />
              </Box>
            )}
            <Box
              sx={{
                flexGrow: 1,
                overflowY: "auto",
                paddingRight: "10px",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                msOverflowStyle: "none", // For Internet Explorer and Edge
                scrollbarWidth: "none", // For Firefox
              }}
            >
              <TextField
                variant="outlined"
                fullWidth
                multiline
                // Remove rows={18} to allow the TextField to expand based on content
                value={leftContent}
                onChange={(e) => setLeftContent(e.target.value)}
                placeholder="Text...."
                disabled={updating}
                InputProps={{
                  sx: { height: "100%" },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "transparent",
                    },
                    "&:hover fieldset": {
                      borderColor: "transparent",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "transparent",
                      border: "none",
                      outline: "none",
                      boxShadow: "none",
                    },
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 2, // Add margin top for spacing
              }}
            >
              {leftContent && (
                <Button
                  variant="contained"
                  onClick={handleUpdate}
                  disabled={updating}
                  sx={{
                    backgroundColor: "#3b82f6",
                    "&:hover": {
                      backgroundColor: "#2563eb",
                    },
                    borderRadius: "0.375rem",
                    boxShadow: 2,
                  }}
                >
                  {updating ? "Updating..." : "Update"}
                </Button>
              )}
            </Box>
          </Card>
        </Grid>

        {/* Right Column: Display Area */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              borderRadius: "8px",
              marginTop: "34px",
              padding: 3,
              position: "relative",
              display: "flex",
              flexDirection: "column",
              height: "75vh",
              overflow: "auto",
              //remove scroll display
              "&::-webkit-scrollbar": {
                display: "none",
              },
            }}
          >
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10,
                  borderRadius: "8px",
                }}
              >
                <CircularProgress size={40} />
              </Box>
            )}
            <Box
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 10,
              }}
            >
              <img
                src={apiSettings.privacyPolicyEdit}
                alt=""
                onClick={handleEdit}
                style={{ cursor: "pointer", opacity: loading ? 0.5 : 1 }}
              />
            </Box>
            <CardContent
              sx={{
                padding: 0,
                paddingTop: 4, // Add padding to the top to prevent overlap
                flexGrow: 1,
              }}
            >
              <Box
                sx={{
                  maxWidth: "100%",
                  color: "#1f2937",
                  // Styling for content within the display box
                  "& h1, h2, h3, h4, h5, h6": {
                    marginBottom: "1rem",
                    fontWeight: "bold",
                  },
                  "& p": {
                    marginBottom: "1rem",
                  },
                }}
              >
                {!loading &&
                  rightContent.split("\n\n").map((paragraph, index) => (
                    <Typography
                      key={index}
                      variant="body1"
                      sx={{
                        marginBottom: "1rem",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {paragraph}
                    </Typography>
                  ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PrivacyPolicy;
